import { Button } from '@repo/ui/components/ui/button'
import { Callout } from '@repo/ui/components/ui/callout'
import { CardContent, CardDescription, CardHeader, CardTitle } from '@repo/ui/components/ui/card'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Activity, AlertTriangle, CreditCard, ExternalLink, Mail, Users } from 'lucide-react'
import type { ComponentType, ReactNode } from 'react'
import { DocumentRouteShell } from '@/components/app/route-shells'
import { PremiumPanel } from '@/components/ui/premium-panel'
import type { RouteStaticData } from '@/lib/router/breadcrumbs'
import { trpc } from '@/lib/trpc'

export const Route = createFileRoute('/_app/admin/')({
  component: AdminOverviewPage,
  staticData: {
    breadcrumbs: [{ label: 'Admin' }],
  } satisfies RouteStaticData,
})

function AdminOverviewPage() {
  const { data: userStats } = trpc.admin.users.stats.useQuery()
  const { data: emailQueueStatus } = trpc.admin.emails.queueStatus.useQuery()

  const statsCards: Array<{
    title: string
    value: number
    description: string
    icon: ComponentType<{ className?: string }>
    iconClassName: string
    action?: ReactNode
  }> = [
    {
      title: 'Workspace users',
      value: userStats?.total || 0,
      description: `${userStats?.admins || 0} admins, ${userStats?.regularUsers || 0} members`,
      icon: Users,
      iconClassName: 'text-primary',
      action: (
        <Button asChild variant="link" size="sm" className="h-auto p-0 text-sm">
          <Link to="/admin/users">Review users</Link>
        </Button>
      ),
    },
    {
      title: 'Email queue pressure',
      value: (emailQueueStatus?.pending || 0) + (emailQueueStatus?.failed || 0),
      description: `${emailQueueStatus?.sent || 0} delivered, ${emailQueueStatus?.failed || 0} failed`,
      icon: Mail,
      iconClassName: 'text-chart-2',
    },
    {
      title: 'System posture',
      value: 100,
      description: 'All critical services operational',
      icon: Activity,
      iconClassName: 'text-chart-2',
    },
  ]

  const externalLinks = [
    {
      title: 'Stripe Dashboard',
      description: 'Revenue operations, plans, and invoicing controls',
      url: 'https://dashboard.stripe.com',
      icon: CreditCard,
    },
    {
      title: 'PostHog Analytics',
      description: 'Behavioral funnels and cohort-level insight',
      url: 'https://app.posthog.com',
      icon: Activity,
    },
  ]

  return (
    <DocumentRouteShell>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {statsCards.map((card) => (
          <PremiumPanel key={card.title} interactive>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
              <card.icon className={`h-4 w-4 ${card.iconClassName}`} />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{card.value}</div>
              <p className="text-xs text-muted-foreground">{card.description}</p>
              {card.action ? <div className="mt-2">{card.action}</div> : null}
            </CardContent>
          </PremiumPanel>
        ))}
      </div>

      <PremiumPanel>
        <CardHeader>
          <CardTitle>External tools</CardTitle>
          <CardDescription>
            Jump into integrated systems for deeper operational diagnostics.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            {externalLinks.map((link) => (
              <PremiumPanel key={link.title} className="transition-colors hover:bg-muted/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <link.icon className="h-5 w-5 text-primary" />
                    <div className="flex-1">
                      <h3 className="font-medium">{link.title}</h3>
                      <p className="text-sm text-muted-foreground">{link.description}</p>
                    </div>
                    <ExternalLink className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <Button asChild variant="link" className="mt-2 h-auto p-0 text-sm">
                    <a href={link.url} target="_blank" rel="noopener noreferrer">
                      Open dashboard
                    </a>
                  </Button>
                </CardContent>
              </PremiumPanel>
            ))}
          </div>
        </CardContent>
      </PremiumPanel>

      <Callout variant="warning" className="app-premium-panel">
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertTriangle className="h-5 w-5" />
          Admin activity feed
        </CardTitle>
        <CardDescription className="mt-2 text-muted-foreground">
          High-impact admin events from the previous 24 hours.
        </CardDescription>
        <div className="mt-3 text-sm text-muted-foreground">
          <p>Real-time audit stream components are being prepared.</p>
          <p className="mt-1 text-xs">
            This surface will include permission mutations, billing escalations, and anomaly alerts.
          </p>
        </div>
      </Callout>
    </DocumentRouteShell>
  )
}
